import { Injectable } from '@angular/core';
import { State, Action, StateContext, Store } from '@ngxs/store';
import { TaskAddModel, Task, TaskTypeToCreate, TaskTypeToList } from '../models/task.model';
import { TaskActions } from './task.actions';
import { TaskService } from '../services/task.service';
import { MappingUtilsService } from 'src/app/services/utils/mapping-utils.service';
import { LocalizationService } from 'src/app/services/localization/localization.service';
import { AlertService } from 'src/app/shared/store/alert/alert.service';
import { patch } from '@ngxs/store/operators';
import { TaskFormFilter } from '../models/task-form-filter.model';
import { MessageFormFilter } from '../models/message-form-filter.model';
import { TaskMessage } from '../models/message.model';
import { TaskStatusSelectors } from 'src/app/shared/store/task-status/task-status.selector';

export class TaskStateModel {
  public tasks: Task[];
  public messages: TaskMessage[];
  public taskTypesToList: TaskTypeToList[];
  public taskTypesToCreate: TaskTypeToCreate[];
  taskForm: {
    model: TaskAddModel;
  };
  taskFilter: {
    model: TaskFormFilter;
  }
  messageFilter: {
    model: MessageFormFilter;
  }
  public isLoadingTasks: boolean;
  public isLoadingMessages: boolean;
  public isListSmaller: boolean;
  public error: string;
}

const defaults = {
  tasks: null,
  messages: null,
  taskTypesToList: [],
  taskTypesToCreate: [],
  taskForm: {
    model: null
  },
  taskFilter: {
    model: null
  },
  messageFilter: {
    model: null
  },
  isLoadingTasks: false,
  isLoadingMessages: false,
  isListSmaller: true,
  error: null
};

@State<TaskStateModel>({
  name: 'task',
  defaults
})
@Injectable()
export class TaskState {

  constructor(
    private taskService: TaskService,
    private alertService: AlertService,
    private localizationService: LocalizationService,
    private store: Store
  ) { }

  @Action(TaskActions.GetTasks)
  getTasks(ctx: StateContext<TaskStateModel>, { filterModel }: TaskActions.GetTasks) {
    ctx.patchState({isLoadingTasks: true});
    return this.taskService.getTasks(filterModel).subscribe({
      next: (response) =>
        ctx.dispatch(new TaskActions.GetTasksSuccess(response)),
      error: (error) =>
        ctx.dispatch(new TaskActions.Error(error)),
    });
  }

  @Action(TaskActions.GetTasksSuccess)
  getTasksSuccess(ctx: StateContext<TaskStateModel>, { tasks }: TaskActions.GetTasksSuccess) {
    const tasksResponse = this.getTaskResponse(tasks);
    ctx.patchState({
      tasks: tasksResponse,
      isLoadingTasks: false,
    });
  }

  @Action(TaskActions.GetMessages)
  getMessages(ctx: StateContext<TaskStateModel>, { filterModel }: TaskActions.GetMessages) {
    ctx.patchState({isLoadingMessages: true});
    return this.taskService.getMessages(filterModel).subscribe({
      next: (response) =>
        ctx.dispatch(new TaskActions.GetMessagesSuccess(response)),
      error: (error) =>
        ctx.dispatch(new TaskActions.Error(error)),
    });
  }

  @Action(TaskActions.GetMessagesSuccess)
  getMessagesSuccess(ctx: StateContext<TaskStateModel>, { messages }: TaskActions.GetMessagesSuccess) {
    const messagesResponse = this.getMessageResponse(messages);
    ctx.patchState({
      messages: messagesResponse,
      isLoadingMessages: false,
    });
  }

  @Action(TaskActions.GetTaskTypesToList)
  GetTaskTypesToList(ctx: StateContext<TaskStateModel>, {  }: TaskActions.GetTaskTypesToList) {
    return this.taskService.getTaskTypesToList().subscribe({
      next: (response) =>
        ctx.dispatch(new TaskActions.GetTaskTypesToListSuccess(response)),
      error: (error) =>
        ctx.dispatch(new TaskActions.Error(error)),
    });
  }

  @Action(TaskActions.GetTaskTypesToListSuccess)
  GetTaskTypesToListSuccess(ctx: StateContext<TaskStateModel>, { taskTypesToList }: TaskActions.GetTaskTypesToListSuccess) {
    ctx.patchState({
      taskTypesToList: taskTypesToList,
      isLoadingTasks: false,
    });
  }

  @Action(TaskActions.GetTaskTypesToCreate)
  GetTaskTypesToCreate(ctx: StateContext<TaskStateModel>, {  }: TaskActions.GetTaskTypesToCreate) {
    return this.taskService.getTaskTypesToCreate().subscribe({
      next: (response) =>
        ctx.dispatch(new TaskActions.GetTaskTypesToCreateSuccess(response)),
      error: (error) =>
        ctx.dispatch(new TaskActions.Error(error)),
    });
  }

  @Action(TaskActions.GetTaskTypesToCreateSuccess)
  GetTaskTypesToCreateSuccess(ctx: StateContext<TaskStateModel>, { taskTypesToCreate }: TaskActions.GetTaskTypesToCreateSuccess) {
    ctx.patchState({
      taskTypesToCreate: taskTypesToCreate,
      isLoadingTasks: false,
    });
  }

  @Action(TaskActions.AddTask)
  addTask(ctx: StateContext<TaskStateModel>, {task, user}: TaskActions.AddTask) {
    this.alertService.confirm(
    this.localizationService.translate("tasks.title"),
    this.localizationService.translate('tasks.taskAddConfirm'),
    this.localizationService.translate('buttons.accept'),
    this.localizationService.translate('buttons.cancel'),
    this.localizationService.translate('tasks.taskAddConfirm')
    ).then((result) => {
      if (result.value) {
        ctx.patchState({isLoadingTasks: true});
        const taskRequest = this.getTaskRequest(task);
        return this.taskService.createTask(taskRequest, user).subscribe({
          next: (response) => {
            ctx.dispatch(new TaskActions.AddTaskSuccess(response))
          },
          error: (error) => {
            ctx.dispatch(new TaskActions.Error(error));
          }
        });
      } else {
        return null;
      }
    });
  }

  @Action(TaskActions.AddTaskSuccess)
  addCenterSuccess(ctx: StateContext<TaskStateModel>, {}: TaskActions.AddTaskSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      isLoadingTasks: false,
    });
  }

  @Action(TaskActions.Error)
  onErrorOcurred(ctx: StateContext<TaskStateModel>, {error}: TaskActions.Error) {
    ctx.patchState({
      error: error,
      isLoadingTasks: false,
    });
    MappingUtilsService.showError(this.getTitle(), error, this.alertService, this.localizationService);
  }

  @Action(TaskActions.SetTaskDefaultState)
  setTasksDefaultState(ctx: StateContext<TaskStateModel>, {}: TaskActions.SetTaskDefaultState) {
    ctx.setState(
      patch({taskForm : patch({
          model: defaults.taskForm.model
        })
      })
    );
  }

  @Action(TaskActions.SetTaskDefaults)
  setTasksDefaults(ctx: StateContext<TaskStateModel>, {}: TaskActions.SetTaskDefaults) {
    ctx.patchState(
      defaults
    );
  }

  private getTitle() {
    return this.localizationService.translate('tasks.title');
  }

  getTaskRequest(task: TaskAddModel) {
    const taskRequest = {
      taskTypeId: Number(task.taskTypeId),
      month: task.month.toString() == "" ? 255 : Number(task.month),
      year: task.year.toString() == "" ? -1 : Number(task.year),
      zoneId: task.zoneId.toString() == "" ? -1 : Number(task.zoneId),
      serviceId: task.serviceId.toString() == "" || task.serviceId.toString() == "0" ? -1 : Number(task.serviceId),
      ceco: task.ceco,
      schoolId: task.schoolId.toString() == "" ? -1 : Number(task.schoolId),
    }
    return taskRequest;
  }

  getTaskResponse(tasks: Task[]) {
    tasks.forEach(task => {
      const status = this.store.selectSnapshot(TaskStatusSelectors.getStatusNameById(task.status));
      if(status) task.statusDescription = this.store.selectSnapshot(TaskStatusSelectors.getStatusNameById(task.status)).description;
      task.startDate = new Date(task.startDate).getDate() == new Date('0001-01-01T00:00:00').getDate() ? null : task.startDate;
      task.endDate = new Date(task.endDate).getDate() == new Date('0001-01-01T00:00:00').getDate() ? null : task.endDate;
    });
    return tasks;
  }

  getMessageResponse(messages: TaskMessage[]) {
    messages.forEach(message => {
      message.processedDate = new Date(message.processedDate).getDate() == new Date('0001-01-01T00:00:00').getDate() ? null : message.processedDate;
    });
    return messages;
  }
}
